<script setup lang="ts">
import { hexAlpha, mixColor, type LevelTier } from '@/utils/levels';
import type { TierBenefit, TierState } from '@/composables/useLevelsProgress';

const props = defineProps<{
  tier: LevelTier;
  state: TierState;
  benefits: TierBenefit[];
  pitch: string;
  /**
   * Nível REAL do jogador. O rodapé "Ativo na sua conta" fala da conta dele,
   * não do elo em foco — sem isso, abrir o Saldo real olhando um elo VIP
   * afirmava que ele estava ativo pra quem nem chegou lá.
   */
  playerLevel: number;
}>();

/** O benefício vale pra CONTA do jogador (independe do elo em foco). */
function isActiveForPlayer(benefit: TierBenefit) {
  return !benefit.pending && benefit.from <= props.playerLevel;
}

/** Qual benefício está com a explicação aberta (o botão "!"). */
const openBenefit = ref<string | null>(null);

function toggle(label: string) {
  openBenefit.value = openBenefit.value === label ? null : label;
}

// Ao trocar de elo, fecha a explicação aberta — senão ela fica pendurada num
// benefício que mudou de estado.
watch(
  () => props.tier.key,
  () => {
    openBenefit.value = null;
  },
);

const sectionTitle = computed(() => {
  // O degrau "Sem elo" não é elo pra ter "benefícios deste elo".
  if (props.tier.isUnranked) return 'Sem elo';

  return props.state === 'locked'
    ? 'O que você desbloqueia'
    : 'Benefícios deste elo';
});

const dividerTag = computed(() => {
  if (props.state === 'locked') return 'AO CHEGAR AQUI';

  return props.state === 'done' ? 'JÁ CONQUISTADOS' : 'TODOS OS BENEFÍCIOS';
});

/**
 * Rótulo de contexto sob o nome do benefício. Pendente devolve vazio de
 * propósito: ninguém sabe ainda quando/como vai liberar — é cadeado e só,
 * sem promessa de nível nem "em breve".
 */
function metricLabel(benefit: TierBenefit) {
  if (benefit.pending) return '';

  if (benefit.isLocked) return `Libera no nível ${benefit.from}`;

  return benefit.isNew
    ? `Liberado ao entrar no ${props.tier.name}`
    : 'Ativo desde os elos anteriores';
}
</script>

<template>
  <div
    class="relative overflow-hidden rounded-t-[18px] pb-4"
    :style="{
      background: `linear-gradient(180deg,${mixColor(tier.color, '#03060F', 0.72)} 0%,${mixColor(
        tier.color,
        '#03060F',
        0.86,
      )} 42%,rgba(3,6,15,.96) 100%)`,
      boxShadow: `inset 0 1px 0 ${hexAlpha(tier.color, 0.3)}`,
    }"
  >
    <span
      aria-hidden="true"
      class="absolute left-0 right-0 top-0 h-0.5 opacity-[0.65]"
      :style="{
        background: `linear-gradient(90deg,transparent,${tier.color} 20%,${tier.color} 80%,transparent)`,
      }"
    ></span>

    <div class="flex flex-col gap-[7px] px-[9px] pt-4 lg:px-[13px]">
      <span class="flex items-center gap-2">
        <NuxtIcon
          class="flex-none text-[17px] text-white"
          filled
          name="gift"
        />
        <span
          class="min-w-0 flex-1 text-[20px] font-extrabold leading-[25px] tracking-[-0.03em] text-white"
        >
          {{ sectionTitle }}
        </span>
      </span>
      <!--
        Altura de 2 linhas reservada (2 × 17px de `leading`): o pitch cai em 1 ou
        2 linhas conforme o elo, e sem a reserva a lista de benefícios inteira
        subia e descia ao trocar de elo.
      -->
      <span
        class="block min-h-[34px] text-[12px] font-normal leading-[17px] text-white/70"
      >
        {{ pitch }}
      </span>
    </div>

    <!-- Etiqueta de seção com a listra diagonal -->
    <div
      class="mb-0.5 mt-[13px] flex items-center gap-[9px] px-[9px] lg:px-[13px]"
    >
      <span
        class="flex-none whitespace-nowrap py-[5px] pl-[11px] pr-4 text-[9.5px] font-bold leading-3 tracking-[0.16em]"
        :style="{
          color: mixColor(tier.color, '#ffffff', 0.72),
          background: `linear-gradient(100deg,${mixColor(tier.color, '#050C22', 0.62)},${mixColor(
            tier.color,
            '#050C22',
            0.3,
          )})`,
          boxShadow: `inset 0 0 0 1px ${hexAlpha(tier.color, 0.5)}`,
          clipPath:
            'polygon(0 0,100% 0,calc(100% - 7px) 50%,100% 100%,0 100%,0 0)',
        }"
      >
        {{ dividerTag }}
      </span>
      <span
        class="h-[7px] min-w-0 flex-1"
        :style="{
          background: `repeating-linear-gradient(115deg,${hexAlpha(tier.color, 0.5)} 0 2px,transparent 2px 7px)`,
          maskImage:
            'linear-gradient(90deg,#000,rgba(0,0,0,.15) 78%,transparent)',
          WebkitMaskImage:
            'linear-gradient(90deg,#000,rgba(0,0,0,.15) 78%,transparent)',
        }"
      ></span>
      <span
        class="h-1.5 w-1.5 flex-none rotate-45"
        :style="{ background: hexAlpha(tier.color, 0.85) }"
      ></span>
    </div>

    <div class="px-[9px] lg:px-[13px]">
      <div
        v-for="(benefit, index) in benefits"
        :key="benefit.label"
        class="-mx-1.5 flex items-center gap-3 rounded-[9px] px-2 py-2.5"
        :style="{
          boxShadow:
            index === 0
              ? 'none'
              : `inset 0 1px 0 ${hexAlpha(tier.color, benefit.isNew ? 0.16 : 0.08)}`,
        }"
      >
        <!-- Check (liberado) ou cadeado (bloqueado) -->
        <span
          class="flex h-[25px] w-[25px] flex-none items-center justify-center rounded-full"
          :style="{
            background: benefit.isLocked
              ? 'rgba(255,255,255,.045)'
              : 'rgba(30,200,120,.16)',
            boxShadow: `inset 0 0 0 1.5px ${
              benefit.isLocked ? 'rgba(255,255,255,.14)' : 'rgba(46,214,133,.6)'
            }`,
          }"
        >
          <NuxtIcon
            v-if="benefit.isLocked"
            class="text-[12px] text-white/[0.55]"
            filled
            name="mini-games/locked"
          />
          <svg
            v-else
            class="block h-[12.5px] w-[12.5px]"
            fill="none"
            stroke="#4FE0A0"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2.6"
            viewBox="0 0 24 24"
          >
            <path d="M5 12.5l4.5 4.5L19 7.5" />
          </svg>
        </span>

        <span class="flex min-w-0 flex-1 flex-col gap-0.5">
          <span
            class="text-[14px] font-medium leading-[19px] tracking-[-0.01em]"
            :style="{
              color: benefit.isLocked ? 'rgba(255,255,255,.6)' : '#fff',
            }"
          >
            {{ benefit.label }}
          </span>
          <span
            v-if="metricLabel(benefit)"
            class="text-[11px] font-normal leading-[15px] text-white/[0.55]"
          >
            {{ metricLabel(benefit) }}
          </span>
          <template v-if="openBenefit === benefit.label">
            <span
              class="mt-[5px] block text-[12px] font-normal leading-[17px] text-white/[0.86]"
            >
              {{ benefit.desc }}
            </span>
            <!-- Pendente não ganha linha de status: cadeado e só. O "ativo"
                 é da CONTA do jogador, não do elo em foco. -->
            <span
              v-if="!benefit.pending"
              class="mt-1 block text-[10px] font-bold uppercase leading-[13px] tracking-[0.1em]"
              :style="{
                color: isActiveForPlayer(benefit)
                  ? '#4FE0A0'
                  : 'rgba(255,255,255,.6)',
              }"
            >
              {{
                isActiveForPlayer(benefit)
                  ? 'Ativo na sua conta'
                  : `Libera no nível ${benefit.from}`
              }}
            </span>
          </template>
        </span>

        <!-- Sem chip de prêmio concreto: a tela não anuncia bônus. O nível em
             que libera já sai no `metricLabel`, sob o nome do benefício. -->

        <button
          aria-label="O que é este benefício"
          class="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-full text-[13px] font-extrabold leading-none"
          :style="{
            background: benefit.isLocked
              ? 'rgba(255,255,255,.06)'
              : hexAlpha(tier.color, 0.18),
            color: benefit.isLocked
              ? 'rgba(255,255,255,.72)'
              : mixColor(tier.color, '#ffffff', 0.62),
            boxShadow: `inset 0 0 0 1px ${
              benefit.isLocked
                ? 'rgba(255,255,255,.16)'
                : hexAlpha(tier.color, 0.4)
            }`,
          }"
          type="button"
          @click="toggle(benefit.label)"
        >
          !
        </button>
      </div>
    </div>
  </div>
</template>
